import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { registerUser, loginUser, verifyEmail, resendVerificationCode } from '../auth/service';
import SuccessModal from './SuccessModal';

interface AuthFormProps {
    isLoginView: boolean;
    onLoginSuccess: (user: User) => void;
    onRegisterSuccess: () => void;
    onShowPrivacyPolicy: () => void;
    externalError?: string | null; // New prop
}

const AuthForm: React.FC<AuthFormProps> = ({ isLoginView, onLoginSuccess, onRegisterSuccess, onShowPrivacyPolicy, externalError }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
    });
    const [verificationCode, setVerificationCode] = useState('');
    const [showVerification, setShowVerification] = useState(false);
    const [emailToVerify, setEmailToVerify] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [passwordCriteria, setPasswordCriteria] = useState({
        minLength: false,
        uppercase: false,
        lowercase: false,
        specialChar: false,
    });

    useEffect(() => {
        // Reset form when switching between login and register
        setFormData({ firstName: '', lastName: '', email: '', password: '' });
        setError(null);
        setPrivacyAccepted(false);
        setPasswordCriteria({ minLength: false, uppercase: false, lowercase: false, specialChar: false });
    }, [isLoginView]);

    const validatePassword = (password: string) => {
        setPasswordCriteria({
            minLength: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        });
    };
    
    const allCriteriaMet = Object.values(passwordCriteria).every(Boolean);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'password') {
            validatePassword(value);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email.trim())) {
            setError("Por favor, informe um e-mail válido.");
            setIsLoading(false);
            return;
        }

        if (isLoginView) {
            try {
                const user = await loginUser(formData.email, formData.password);
                onLoginSuccess(user);
            } catch (err: any) {
                if (err.response?.data?.requiresVerification) {
                    setEmailToVerify(err.response.data.email);
                    setShowVerification(true);
                    setError(null);
                } else if (err.message === 'Invalid credentials') {
                    setError('Usuário ou senha Incorreta.');
                } else {
                    setError(err.message);
                }
            } finally {
                setIsLoading(false);
            }
        } else { // Register view
            try {
                if (!allCriteriaMet) {
                    throw new Error("A senha não atende a todos os critérios.");
                }
                if (!privacyAccepted) {
                    throw new Error("Você deve aceitar a política de privacidade.");
                }
                const response = await registerUser(formData.firstName, formData.lastName, formData.email, formData.password);
                setEmailToVerify(response.email);
                setShowVerification(true);
                setSuccessMessage("Um código foi enviado para seu e-mail.");
            } catch (err: any) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        try {
            await verifyEmail(emailToVerify, verificationCode);
            setShowVerification(false);
            setShowSuccessModal(true);
        } catch (err: any) {
            setError(err.response?.data?.message || "Falha na verificação.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = async () => {
        setError(null);
        setIsLoading(true);
        try {
            await resendVerificationCode(emailToVerify);
            setSuccessMessage("Novo código enviado!");
        } catch (err: any) {
            setError("Erro ao reenviar código.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmSuccess = () => {
        setShowSuccessModal(false);
        onRegisterSuccess();
    };
    
    const renderPasswordCriteria = () => {
        const criteria = [
            { met: passwordCriteria.minLength, text: 'Pelo menos 8 caracteres' },
            { met: passwordCriteria.uppercase, text: 'Uma letra maiúscula' },
            { met: passwordCriteria.lowercase, text: 'Uma letra minúscula' },
            { met: passwordCriteria.specialChar, text: 'Um caractere especial' },
        ];
        return (
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-left mt-2">
                {criteria.map(c => (
                     <div key={c.text} className={`transition-colors ${c.met ? 'text-green-400' : 'text-slate-400'}`}>
                        <i className={`fas ${c.met ? 'fa-check-circle' : 'fa-times-circle'} mr-2`}></i>
                        {c.text}
                    </div>
                ))}
            </div>
        );
    }

    return (
        <>
            <SuccessModal
                isOpen={showSuccessModal}
                title="Sucesso!"
                onConfirm={handleConfirmSuccess}
                buttonText="Efetuar Login"
            >
                E-mail verificado com sucesso! Agora você pode fazer login.
            </SuccessModal>
            
            {showVerification ? (
                <form onSubmit={handleVerifyCode} className="w-full space-y-4 text-left">
                    <div className="text-center mb-4">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Verifique seu E-mail</h3>
                        <p className="text-sm text-gray-600 dark:text-slate-400">Enviamos um código de 6 dígitos para <strong>{emailToVerify}</strong></p>
                    </div>
                    
                    <div>
                        <label htmlFor="code" className="block text-sm font-medium text-gray-700 dark:text-slate-300">Código de Verificação</label>
                        <input 
                            type="text" 
                            name="code" 
                            id="code" 
                            value={verificationCode} 
                            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            required 
                            className="mt-1 block w-full px-3 py-3 text-center text-2xl tracking-widest bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                        />
                    </div>

                    {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                    {successMessage && <p className="text-green-500 text-sm text-center">{successMessage}</p>}

                    <button
                        type="submit"
                        disabled={isLoading || verificationCode.length < 6}
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:bg-slate-600"
                    >
                        {isLoading ? 'Verificando...' : 'Verificar E-mail'}
                    </button>

                    <div className="text-center mt-4">
                        <button
                            type="button"
                            onClick={handleResendCode}
                            className="text-sm text-orange-500 hover:text-orange-400 underline"
                        >
                            Não recebeu o código? Reenviar
                        </button>
                    </div>
                    <div className="text-center">
                        <button
                            type="button"
                            onClick={() => setShowVerification(false)}
                            className="text-xs text-gray-500 hover:text-gray-400"
                        >
                            Voltar
                        </button>
                    </div>
                </form>
            ) : (
                <form onSubmit={handleSubmit} className="w-full space-y-4 text-left">
                    {!isLoginView && (
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-slate-300">Nome</label>
                            <input type="text" name="firstName" id="firstName" value={formData.firstName} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500"/>
                        </div>
                        <div className="flex-1">
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-slate-300">Sobrenome</label>
                            <input type="text" name="lastName" id="lastName" value={formData.lastName} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500"/>
                        </div>
                    </div>
                )}
                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-slate-300">E-mail</label>
                    <input type="email" name="email" id="email" value={formData.email} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500"/>
                </div>
                <div>
                    <label htmlFor="password"className="block text-sm font-medium text-gray-700 dark:text-slate-300">Senha</label>
                    <input type="password" name="password" id="password" value={formData.password} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500"/>
                    {!isLoginView && renderPasswordCriteria()}
                </div>

                {!isLoginView && (
                    <div className="flex items-center">
                        <input
                            id="privacy"
                            name="privacy"
                            type="checkbox"
                            checked={privacyAccepted}
                            onChange={(e) => setPrivacyAccepted(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <label htmlFor="privacy" className="ml-2 block text-sm text-gray-900 dark:text-slate-300">
                            Eu li e aceito a {' '}
                            <button
                                type="button"
                                onClick={onShowPrivacyPolicy}
                                className="font-medium text-orange-500 hover:text-orange-400 underline"
                            >
                                Política de Privacidade
                            </button>
                            .
                        </label>
                    </div>
                )}

                {(error || externalError) && !showVerification && <p className="text-red-500 text-sm text-center">{error || externalError}</p>}
                
                <div className="pt-2">
                    <button
                        type="submit"
                        disabled={isLoading || (!isLoginView && (!allCriteriaMet || !privacyAccepted))}
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:bg-slate-600 disabled:cursor-not-allowed"
                    >
                        {isLoading ? 'Processando...' : (isLoginView ? 'Entrar' : 'Cadastrar')}
                    </button>
                </div>
            </form>
            )}
        </>
    );
};

export default AuthForm;